import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FiArrowLeft,
  FiDownload,
  FiMessageCircle,
  FiPrinter,
  FiRefreshCw,
} from "react-icons/fi";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";

import {
  fetchSaleInvoiceById,
  saleInvoiceLoadingSelector,
  selectedSaleInvoiceSelector,
} from "../../../../../../store/billing";


import "./SaleInvoicePrintPage.css";
import useAuth from "../../../../../../hooks/useAuth";

import {
  CompactInvoiceTemplate,
  RetailInvoiceTemplate,
  ServiceInvoiceTemplate,
  ThermalInvoiceTemplate,
  ProfessionalInvoiceTemplate,
  MinimalInvoiceTemplate,
  InvoiceSignature,
  getBusinessInitials,
  resolveBusinessLogo,
  INVOICE_TEMPLATE_CODES,
} from "./templates";



/*
|--------------------------------------------------------------------------
| GENERIC HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
  value,
  fallback = 0
) => {
  const number =
    Number(value);

  return Number.isFinite(
    number
  )
    ? number
    : fallback;
};

const roundMoney = (
  value
) => {
  return (
    Math.round(
      (
        toNumber(value) +
        Number.EPSILON
      ) *
        100
    ) / 100
  );
};

const getNestedValue = (
  object,
  path
) => {
  if (
    !object ||
    !path
  ) {
    return undefined;
  }

  return String(path)
    .split(".")
    .reduce(
      (
        result,
        key
      ) =>
        result?.[key],
      object
    );
};

const getFirstValue = (
  object,
  paths = [],
  fallback = ""
) => {
  for (
    const path of paths
  ) {
    const value =
      getNestedValue(
        object,
        path
      );

    if (
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      return value;
    }
  }

  return fallback;
};

const formatCurrency = (
  value
) => {
  return new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  ).format(
    toNumber(value)
  );
};

const formatNumber = (
  value,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2
) => {
  return new Intl.NumberFormat(
    "en-IN",
    {
      minimumFractionDigits,
      maximumFractionDigits,
    }
  ).format(
    toNumber(value)
  );
};


/*
|--------------------------------------------------------------------------
| NUMBER TO WORDS
|--------------------------------------------------------------------------
|
| Used by the alternate invoice templates.
| This intentionally supports invoice-sized positive integer values.
|
|--------------------------------------------------------------------------
*/

const numberToWords = (
  value
) => {
  const number =
    Math.floor(
      Math.abs(
        toNumber(value)
      )
    );

  if (number === 0) {
    return "Zero";
  }

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const belowHundred =
    (input) => {
      if (input < 20) {
        return ones[input];
      }

      return [
        tens[
          Math.floor(
            input / 10
          )
        ],
        ones[
          input % 10
        ],
      ]
        .filter(Boolean)
        .join(" ");
    };

  const belowThousand =
    (input) => {
      if (input < 100) {
        return belowHundred(
          input
        );
      }

      return [
        `${
          ones[
            Math.floor(
              input / 100
            )
          ]
        } Hundred`,
        belowHundred(
          input % 100
        ),
      ]
        .filter(Boolean)
        .join(" ");
    };

  const parts = [];

  let remaining =
    number;

  const crore =
    Math.floor(
      remaining /
      10000000
    );

  if (crore) {
    parts.push(
      `${
        numberToWords(
          crore
        )
      } Crore`
    );

    remaining %=
      10000000;
  }

  const lakh =
    Math.floor(
      remaining /
      100000
    );

  if (lakh) {
    parts.push(
      `${
        belowThousand(
          lakh
        )
      } Lakh`
    );

    remaining %=
      100000;
  }

  const thousand =
    Math.floor(
      remaining /
      1000
    );

  if (thousand) {
    parts.push(
      `${
        belowThousand(
          thousand
        )
      } Thousand`
    );

    remaining %=
      1000;
  }

  if (remaining) {
    parts.push(
      belowThousand(
        remaining
      )
    );
  }

  return parts
    .filter(Boolean)
    .join(" ");
};


const formatDate = (
  value
) => {
  if (!value) {
    return "-";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  ).format(date);
};

const formatLabel = (
  value
) => {
  if (!value) {
    return "-";
  }

  return String(value)
    .replace(
      /_/g,
      " "
    )
    .toLowerCase()
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
};

const getAddressText = (
  address
) => {
  if (!address) {
    return "-";
  }

  if (
    typeof address ===
    "string"
  ) {
    return (
      address.trim() ||
      "-"
    );
  }

  return [
    address.addressLine1 ||
      address.line1,
    address.addressLine2 ||
      address.line2,
    address.address,
    address.city,
    address.state,
    address.pincode ||
      address.postalCode,
    address.country,
  ]
    .filter(Boolean)
    .join(", ") || "-";
};

const getInitial = (
  value
) => {

  return getBusinessInitials({
    businessName:
      value,
  });

};

/*
|--------------------------------------------------------------------------
| ITEM NORMALIZATION
|--------------------------------------------------------------------------
*/

const normalizePrintItem = (
  rawItem,
  index
) => {
  const quantity =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "quantity",
          "qty",
        ],
        0
      )
    );

  const rate =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "rate",
          "unitPrice",
          "price",
          "sellingPrice",
          "productSnapshot.sellingPrice",
          "productSnapshot.price",
        ],
        0
      )
    );

  const grossAmount =
    roundMoney(
      toNumber(
        getFirstValue(
          rawItem,
          [
            "grossAmount",
            "subtotal",
            "subTotal",
            "baseAmount",
          ],
          quantity *
            rate
        )
      )
    );

  const discountType =
    getFirstValue(
      rawItem,
      [
        "discountType",
        "discount.type",
      ],
      "NONE"
    );

  const discountValue =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "discountValue",
          "discount.value",
          "discountPercentage",
          "discountRate",
        ],
        0
      )
    );

  let discountAmount =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "discountAmount",
          "lineDiscountAmount",
          "calculatedDiscount",
          "discount.amount",
          "discount.discountAmount",
        ],
        0
      )
    );

  if (
    discountAmount === 0 &&
    discountValue > 0
  ) {
    discountAmount =
      String(
        discountType
      ).toUpperCase() ===
      "PERCENTAGE"
        ? roundMoney(
            grossAmount *
              discountValue /
              100
          )
        : Math.min(
            roundMoney(
              discountValue
            ),
            grossAmount
          );
  }

  const priceMode =
    String(
      getFirstValue(
        rawItem,
        [
          "priceMode",
        ],
        "TAX_EXCLUSIVE"
      )
    ).toUpperCase();

  const taxPercentage =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "taxPercentage",
          "taxRate",
          "gstRate",
          "tax.percentage",
        ],
        0
      )
    );

  const cessPercentage =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "cessPercentage",
          "cessRate",
        ],
        0
      )
    );

  const discountedAmount =
    roundMoney(
      Math.max(
        grossAmount -
          discountAmount,
        0
      )
    );

  let taxableAmount =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "taxableAmount",
          "netTaxableAmount",
        ],
        NaN
      ),
      NaN
    );

  let taxAmount =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "taxAmount",
          "calculatedTax",
          "tax.amount",
          "gstAmount",
        ],
        NaN
      ),
      NaN
    );

  if (
    !Number.isFinite(
      taxableAmount
    ) ||
    !Number.isFinite(
      taxAmount
    )
  ) {
    if (
      priceMode ===
      "TAX_INCLUSIVE"
    ) {
      const combinedRate =
        taxPercentage +
        cessPercentage;

      taxableAmount =
        combinedRate > 0
          ? roundMoney(
              discountedAmount /
                (
                  1 +
                  combinedRate /
                    100
                )
            )
          : discountedAmount;

      taxAmount =
        roundMoney(
          discountedAmount -
            taxableAmount
        );
    } else {
      taxableAmount =
        discountedAmount;

      taxAmount =
        roundMoney(
          taxableAmount *
            taxPercentage /
            100
        );
    }
  }

  const cessAmount =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "cessAmount",
        ],
        priceMode ===
          "TAX_EXCLUSIVE"
          ? roundMoney(
              taxableAmount *
                cessPercentage /
                100
            )
          : 0
      )
    );

  const calculatedTotal =
    priceMode ===
    "TAX_INCLUSIVE"
      ? discountedAmount
      : roundMoney(
          taxableAmount +
            taxAmount +
            cessAmount
        );

  const totalAmount =
    roundMoney(
      toNumber(
        getFirstValue(
          rawItem,
          [
            "totalAmount",
            "lineTotal",
            "grandTotal",
            "netAmount",
            "amount",
          ],
          calculatedTotal
        )
      )
    );

  const itemName =
    getFirstValue(
      rawItem,
      [
        "itemName",
        "productName",
        "name",
        "title",
        "productSnapshot.name",
        "productSnapshot.productName",
        "product.name",
        "product.productName",
      ],
      ""
    ) ||
    getFirstValue(
      rawItem,
      [
        "description",
      ],
      "Manual Item"
    );

  return {
    id:
      rawItem?._id ||
      rawItem?.id ||
      index,

    itemName,

    description:
      getFirstValue(
        rawItem,
        [
          "description",
          "itemDescription",
        ],
        ""
      ),

    hsnSacCode:
      getFirstValue(
        rawItem,
        [
          "hsnSacCode",
          "hsnCode",
          "sacCode",
          "productSnapshot.hsnSacCode",
          "productSnapshot.hsnCode",
          "product.hsnSacCode",
          "product.hsnCode",
        ],
        "-"
      ),

    quantity,

    unit:
      getFirstValue(
        rawItem,
        [
          "unit",
          "uom",
          "unitName",
        ],
        ""
      ),

    rate,
    grossAmount,
    discountType,
    discountValue,
    discountAmount,
    taxableAmount,
    taxPercentage,
    taxAmount,
    cessAmount,
    totalAmount,

    isManual:
      !rawItem?.productId ||
      String(
        rawItem?.itemType ||
        ""
      ).toUpperCase() ===
        "MANUAL",
  };
};

/*
|--------------------------------------------------------------------------
| INVOICE TEMPLATE COMPONENTS
|--------------------------------------------------------------------------
*/

const INVOICE_TEMPLATE_COMPONENTS = {
  [INVOICE_TEMPLATE_CODES.COMPACT]:
    CompactInvoiceTemplate,

  [INVOICE_TEMPLATE_CODES.RETAIL]:
    RetailInvoiceTemplate,

  [INVOICE_TEMPLATE_CODES.SERVICE]:
    ServiceInvoiceTemplate,

  [INVOICE_TEMPLATE_CODES.THERMAL]:
    ThermalInvoiceTemplate,

  [INVOICE_TEMPLATE_CODES.PROFESSIONAL]:
    ProfessionalInvoiceTemplate,

  [INVOICE_TEMPLATE_CODES.MINIMAL]:
    MinimalInvoiceTemplate,
};


const normalizeInvoiceTemplateCode = (
  value
) => {
  const normalized =
    String(
      value ||
      ""
    )
      .trim()
      .toUpperCase();

  if (
    normalized ===
    INVOICE_TEMPLATE_CODES.MODERN_GST
  ) {
    return normalized;
  }

  if (
    INVOICE_TEMPLATE_COMPONENTS[
      normalized
    ]
  ) {
    return normalized;
  }

  return (
    INVOICE_TEMPLATE_CODES
      .MODERN_GST
  );
};


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SaleInvoicePrintPage =
  () => {
    const dispatch =
      useDispatch();

    const history =
      useHistory();

    const location =
      useLocation();

    const { id } =
      useParams();

    /*
    |--------------------------------------------------------------------------
    | PRINT TARGET
    |--------------------------------------------------------------------------
    |
    | Keep a direct reference to the actual invoice sheet. This avoids relying
    | on Admin Console layout wrappers while opening the browser print dialog.
    |
    |--------------------------------------------------------------------------
    */

    const printRef =
      useRef(null);

    const { user } =
      useAuth();

    const tenant =
      user?.tenant || {};


    const saleInvoice =
      useSelector(
        selectedSaleInvoiceSelector
      );

    const loadingState =
      useSelector(
        saleInvoiceLoadingSelector
      ) || {};

    const [
      actionMessage,
      setActionMessage,
    ] = useState("");

    const loading =
      Boolean(
        loadingState.detailsLoading ||
        loadingState.fetchByIdLoading ||
        loadingState.loading
      );

    /*
    |--------------------------------------------------------------------------
    | LOAD TAX INVOICE
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
      if (id) {
        dispatch(
          fetchSaleInvoiceById(
            id
          )
        );
      }
    }, [
      dispatch,
      id,
    ]);

    /*
    |--------------------------------------------------------------------------
    | NORMALIZED DATA
    |--------------------------------------------------------------------------
    */

    const rawItems =
      useMemo(() => {
        const source =
          getFirstValue(
            saleInvoice,
            [
              "items",
              "document.items",
              "saleInvoice.items",
            ],
            []
          );

        return Array.isArray(
          source
        )
          ? source
          : [];
      }, [
        saleInvoice,
      ]);

    const items =
      useMemo(
        () =>
          rawItems.map(
            (
              item,
              index
            ) =>
              normalizePrintItem(
                item,
                index
              )
          ),
        [
          rawItems,
        ]
      );

    const totals =
      useMemo(() => {
        const calculated =
          items.reduce(
            (
              result,
              item
            ) => ({
              subtotal:
                result.subtotal +
                item.grossAmount,

              totalDiscount:
                result.totalDiscount +
                item.discountAmount,

              taxableAmount:
                result.taxableAmount +
                item.taxableAmount,

              totalTax:
                result.totalTax +
                item.taxAmount,

              cessAmount:
                result.cessAmount +
                item.cessAmount,

              grandTotal:
                result.grandTotal +
                item.totalAmount,
            }),
            {
              subtotal: 0,
              totalDiscount: 0,
              taxableAmount: 0,
              totalTax: 0,
              cessAmount: 0,
              grandTotal: 0,
            }
          );

        const savedSubtotal =
          toNumber(
            getFirstValue(
              saleInvoice,
              [
                "totals.subtotal",
                "totals.subTotal",
                "totals.grossAmount",
                "subtotal",
                "subTotal",
                "grossAmount",
              ],
              0
            )
          );

        const subtotal =
          savedSubtotal > 0
            ? savedSubtotal
            : calculated.subtotal;

        const savedDiscount =
          toNumber(
            getFirstValue(
              saleInvoice,
              [
                "totals.discountAmount",
                "totals.totalDiscount",
                "discountAmount",
                "totalDiscount",
              ],
              0
            )
          );

        const totalDiscount =
          savedDiscount > 0
            ? savedDiscount
            : calculated.totalDiscount;

        const savedTaxable =
          toNumber(
            getFirstValue(
              saleInvoice,
              [
                "totals.taxableAmount",
                "taxableAmount",
              ],
              0
            )
          );

        const taxableAmount =
          savedTaxable > 0
            ? savedTaxable
            : calculated.taxableAmount;

        const cgstAmount =
          toNumber(
            getFirstValue(
              saleInvoice,
              [
                "totals.cgstAmount",
                "cgstAmount",
              ],
              0
            )
          );

        const sgstAmount =
          toNumber(
            getFirstValue(
              saleInvoice,
              [
                "totals.sgstAmount",
                "sgstAmount",
              ],
              0
            )
          );

        const igstAmount =
          toNumber(
            getFirstValue(
              saleInvoice,
              [
                "totals.igstAmount",
                "igstAmount",
              ],
              0
            )
          );

        const savedTotalTax =
          toNumber(
            getFirstValue(
              saleInvoice,
              [
                "totals.taxAmount",
                "totals.totalTax",
                "taxAmount",
                "totalTax",
              ],
              0
            )
          );

        const totalTax =
          savedTotalTax > 0
            ? savedTotalTax
            : calculated.totalTax;

        const roundOffAmount =
          toNumber(
            getFirstValue(
              saleInvoice,
              [
                "totals.roundOffAmount",
                "totals.roundOff",
                "roundOffAmount",
                "roundOff",
              ],
              0
            )
          );

        const savedGrandTotal =
          toNumber(
            getFirstValue(
              saleInvoice,
              [
                "totals.grandTotal",
                "totals.totalAmount",
                "grandTotal",
                "totalAmount",
                "netAmount",
              ],
              0
            )
          );

        const grandTotal =
          savedGrandTotal > 0
            ? savedGrandTotal
            : roundMoney(
                calculated.grandTotal +
                  roundOffAmount
              );

        const isInterState =
          igstAmount > 0 ||
          String(
            getFirstValue(
              saleInvoice,
              [
                "taxType",
                "supplyType",
              ],
              ""
            )
          ).toUpperCase() ===
            "INTER_STATE";

        let finalCgst =
          cgstAmount;

        let finalSgst =
          sgstAmount;

        let finalIgst =
          igstAmount;

        if (
          totalTax > 0 &&
          cgstAmount === 0 &&
          sgstAmount === 0 &&
          igstAmount === 0
        ) {
          if (
            isInterState
          ) {
            finalIgst =
              totalTax;
          } else {
            finalCgst =
              roundMoney(
                totalTax / 2
              );

            finalSgst =
              roundMoney(
                totalTax -
                  finalCgst
              );
          }
        }

        return {
          subtotal:
            roundMoney(
              subtotal
            ),

          totalDiscount:
            roundMoney(
              totalDiscount
            ),

          taxableAmount:
            roundMoney(
              taxableAmount
            ),

          cgstAmount:
            roundMoney(
              finalCgst
            ),

          sgstAmount:
            roundMoney(
              finalSgst
            ),

          igstAmount:
            roundMoney(
              finalIgst
            ),

          totalTax:
            roundMoney(
              totalTax
            ),

          roundOffAmount:
            roundMoney(
              roundOffAmount
            ),

          grandTotal:
            roundMoney(
              grandTotal
            ),

          isInterState:
            finalIgst > 0,
        };
      }, [
        saleInvoice,
        items,
      ]);

    const company =
      useMemo(() => {
        const tenantAddress =
          tenant?.address || {};

        const tenantBranding =
          tenant?.branding || {};

        const tenantSettings =
          tenant?.settings || {};

        const documentCompany =
          getFirstValue(
            saleInvoice,
            [
              "companySnapshot",
              "businessSnapshot",
              "firmSnapshot",
              "company",
              "firm",
            ],
            {}
          ) || {};

        return {
          name:
            String(
              tenant?.legalName ||
              tenant?.name ||
              getFirstValue(
                documentCompany,
                [
                  "businessName",
                  "companyName",
                  "firmName",
                  "legalName",
                  "name",
                ],
                "Business"
              )
            )
              .trim()
              .toUpperCase(),

          legalName:
            String(
              tenant?.legalName ||
              tenant?.name ||
              ""
            )
              .trim()
              .toUpperCase(),

          code:
            tenant?.code ||
            "",

          address:
            getAddressText({
              line1:
                tenantAddress?.line1 ||
                "",
              line2:
                tenantAddress?.line2 ||
                "",
              city:
                tenantAddress?.city ||
                "",
              state:
                tenantAddress?.state ||
                tenantSettings?.state ||
                "",
              postalCode:
                tenantAddress?.postalCode ||
                "",
              country:
                tenantAddress?.country ||
                tenantSettings?.country ||
                "",
            }) !== "-"
              ? getAddressText({
                  line1:
                    tenantAddress?.line1 ||
                    "",
                  line2:
                    tenantAddress?.line2 ||
                    "",
                  city:
                    tenantAddress?.city ||
                    "",
                  state:
                    tenantAddress?.state ||
                    tenantSettings?.state ||
                    "",
                  postalCode:
                    tenantAddress?.postalCode ||
                    "",
                  country:
                    tenantAddress?.country ||
                    tenantSettings?.country ||
                    "",
                })
              : getAddressText(
                  getFirstValue(
                    documentCompany,
                    [
                      "address",
                      "registeredAddress",
                      "billingAddress",
                    ],
                    ""
                  )
                ),

          mobile:
            tenant?.mobile ||
            getFirstValue(
              documentCompany,
              [
                "mobile",
                "phone",
                "contactNumber",
              ],
              ""
            ),

          email:
            tenant?.email ||
            getFirstValue(
              documentCompany,
              [
                "email",
              ],
              ""
            ),

          gstin:
            tenant?.gstNumber ||
            getFirstValue(
              documentCompany,
              [
                "gstin",
                "gstNumber",
              ],
              ""
            ),

          pan:
            tenant?.panNumber ||
            getFirstValue(
              documentCompany,
              [
                "pan",
                "panNumber",
              ],
              ""
            ),

          logo:
            tenantBranding?.logo ||
            tenant?.invoiceBranding?.logo ||
            getFirstValue(
              documentCompany,
              [
                "logo",
                "logoUrl",
                "businessLogo",
              ],
              ""
            ),

          currency:
            tenantSettings?.currency ||
            "INR",

          timezone:
            tenantSettings?.timezone ||
            "Asia/Kolkata",

          dateFormat:
            tenantSettings?.dateFormat ||
            "DD-MM-YYYY",
        };
      }, [
        saleInvoice,
        tenant,
      ]);

    const customer =
      useMemo(() => {
        const savedCustomer =
          getFirstValue(
            saleInvoice,
            [
              "customer",
            ],
            {}
          ) || {};

        const snapshot =
          getFirstValue(
            saleInvoice,
            [
              "customerSnapshot",
            ],
            {}
          ) || {};

        const billingAddress =
          getFirstValue(
            saleInvoice,
            [
              "billingAddressSnapshot",
              "billingAddress",
            ],
            {}
          );

        const shippingAddress =
          getFirstValue(
            saleInvoice,
            [
              "shippingAddressSnapshot",
              "shippingAddress",
            ],
            billingAddress
          );

        return {
          name:
            getFirstValue(
              snapshot,
              [
                "customerName",
                "name",
                "companyName",
              ],
              getFirstValue(
                savedCustomer,
                [
                  "customerName",
                  "name",
                  "companyName",
                ],
                "Walk-in Customer"
              )
            ),

          mobile:
            getFirstValue(
              snapshot,
              [
                "mobile",
                "phone",
              ],
              getFirstValue(
                savedCustomer,
                [
                  "mobile",
                  "phone",
                ],
                ""
              )
            ),

          email:
            getFirstValue(
              snapshot,
              [
                "email",
              ],
              getFirstValue(
                savedCustomer,
                [
                  "email",
                ],
                ""
              )
            ),

          gstin:
            getFirstValue(
              snapshot,
              [
                "gstin",
                "gstNumber",
              ],
              getFirstValue(
                savedCustomer,
                [
                  "gstin",
                  "gstNumber",
                ],
                ""
              )
            ),

          billingAddress:
            getAddressText(
              billingAddress
            ),

          shippingAddress:
            getAddressText(
              shippingAddress
            ),
        };
      }, [
        saleInvoice,
      ]);

    const documentNumber =
      getFirstValue(
        saleInvoice,
        [
          "documentNumber",
          "invoiceNumber",
          "invoiceNo",
          "saleInvoiceNumber",
          "saleInvoiceNo",
          "number",
        ],
        "-"
      );

    const status =
      getFirstValue(
        saleInvoice,
        [
          "status",
          "documentStatus",
        ],
        "DRAFT"
      );

    const documentDate =
      getFirstValue(
        saleInvoice,
        [
          "documentDate",
          "invoiceDate",
          "saleInvoiceDate",
          "createdAt",
        ],
        ""
      );

    const dueDate =
      getFirstValue(
        saleInvoice,
        [
          "dueDate",
          "paymentDueDate",
        ],
        ""
      );

    const salesChannel =
      getFirstValue(
        saleInvoice,
        [
          "salesChannel",
        ],
        "-"
      );

    const priceMode =
      getFirstValue(
        saleInvoice,
        [
          "priceMode",
        ],
        "-"
      );

    const notes =
      getFirstValue(
        saleInvoice,
        [
          "customerNotes",
          "notes",
        ],
        ""
      );

    const terms =
      getFirstValue(
        saleInvoice,
        [
          "termsAndConditions",
          "terms",
        ],
        ""
      );


    /*
    |--------------------------------------------------------------------------
    | SELECTED INVOICE TEMPLATE
    |--------------------------------------------------------------------------
    |
    | Priority:
    |
    | 1. URL query: ?template=PROFESSIONAL
    |    Useful for Settings preview without changing the saved default.
    |
    | 2. Invoice snapshot/template field.
    |
    | 3. Business/company snapshot template field.
    |
    | 4. Temporary browser preference.
    |
    | 5. Existing MODERN_GST layout.
    |
    | When the backend tenant setting API is connected, place its value in
    | the invoice/business response and this page will use it automatically.
    |
    |--------------------------------------------------------------------------
    */

    const selectedTemplateCode =
      useMemo(() => {
        const params =
          new URLSearchParams(
            location.search
          );

        const queryTemplate =
          params.get(
            "template"
          );

        const invoiceTemplate =
          getFirstValue(
            saleInvoice,
            [
              "templateCode",
              "invoiceTemplate",
              "invoiceTemplateCode",
              "printTemplate",
              "settings.invoiceTemplate",
              "billingSettings.invoiceTemplate",
              "companySnapshot.invoiceTemplate",
              "businessSnapshot.invoiceTemplate",
            ],
            ""
          );

        const tenantTemplate =
          tenant
            ?.settings
            ?.invoiceTemplate ||
          "";

        return normalizeInvoiceTemplateCode(
          queryTemplate ||
          invoiceTemplate ||
          tenantTemplate ||
          INVOICE_TEMPLATE_CODES
            .MODERN_GST
        );
      }, [
        location.search,
        saleInvoice,
        tenant,
      ]);


    const SelectedInvoiceTemplate =
      selectedTemplateCode ===
      INVOICE_TEMPLATE_CODES
        .MODERN_GST
        ? null
        : INVOICE_TEMPLATE_COMPONENTS[
            selectedTemplateCode
          ] ||
          null;


    /*
    |--------------------------------------------------------------------------
    | TEMPLATE DATA ADAPTER
    |--------------------------------------------------------------------------
    |
    | Your existing SaleInvoicePrintPage keeps normalized item calculations
    | flat. Alternate template files support a calculation object, so adapt
    | the existing data once here instead of changing your billing logic.
    |
    |--------------------------------------------------------------------------
    */

    const templateItems =
      useMemo(
        () =>
          items.map(
            (
              item
            ) => ({
              ...item,

              _id:
                item.id,

              calculation: {
                quantity:
                  item.quantity,

                rate:
                  item.rate,

                grossAmount:
                  item.grossAmount,

                discountAmount:
                  item.discountAmount,

                taxableAmount:
                  item.taxableAmount,

                taxAmount:
                  item.taxAmount,

                totalAmount:
                  item.totalAmount,
              },
            })
          ),
        [
          items,
        ]
      );


    const paidAmount =
      toNumber(
        getFirstValue(
          saleInvoice,
          [
            "paidAmount",
            "paymentSummary.paidAmount",
            "totals.paidAmount",
            "receivedAmount",
          ],
          0
        )
      );


    const balanceAmount =
      roundMoney(
        toNumber(
          getFirstValue(
            saleInvoice,
            [
              "balanceAmount",
              "paymentSummary.balanceAmount",
              "totals.balanceAmount",
              "balanceDue",
            ],
            Math.max(
              totals.grandTotal -
              paidAmount,
              0
            )
          )
        )
      );


    const templateTotals =
      useMemo(
        () => ({
          ...totals,

          grossAmount:
            totals.subtotal,

          itemGrossAmount:
            totals.subtotal,

          itemDiscountAmount:
            totals.totalDiscount,

          invoiceDiscountAmount:
            0,

          taxAmount:
            totals.totalTax,
        }),
        [
          totals,
        ]
      );


    const templateProps = {
      invoice:
        saleInvoice,

      invoiceNumber:
        documentNumber,

      businessSnapshot: {
        ...company,

        businessName:
          company.name,

        name:
          company.name,

        address:
          company.address,

        gstin:
          company.gstin,

        mobile:
          company.mobile,

        email:
          company.email,

        branding:
          tenant
            ?.branding ||
          {},

        invoiceBranding:
          tenant
            ?.invoiceBranding ||
          {},

        logo:
          tenant
            ?.invoiceBranding
            ?.logo ||
          company.logo ||
          tenant
            ?.branding
            ?.logo ||
          "",

        signature:
          tenant
            ?.invoiceBranding
            ?.signatureImage ||
          "",

        signatureMode:
          tenant
            ?.invoiceBranding
            ?.signatureMode ||
          "NONE",

        signatureText:
          tenant
            ?.invoiceBranding
            ?.signatureText ||
          "",

        signatureStyle:
          tenant
            ?.invoiceBranding
            ?.signatureStyle ||
          "STYLE_1",
      },

      customerName:
        customer.name,

      customerSnapshot: {
        name:
          customer.name,

        customerName:
          customer.name,

        mobile:
          customer.mobile,

        email:
          customer.email,

        gstin:
          customer.gstin,
      },

      billingAddress:
        customer.billingAddress,

      shippingAddress:
        customer.shippingAddress,

      calculatedItems:
        templateItems,

      totals:
        templateTotals,

      grandTotal:
        totals.grandTotal,

      paidAmount,

      balanceAmount,

      displayCurrency:
        formatCurrency,

      formatAddress:
        getAddressText,

      formatDate,

      formatLabel,

      formatNumber,

      numberToWords,
    };


    /*
    |--------------------------------------------------------------------------
    | PRINT PAGE TEMPLATE OPTIONS
    |--------------------------------------------------------------------------
    |
    | This selector is only for preview / print-page switching right now.
    | The tenant default can later come from Settings > Invoice Template API.
    |
    |--------------------------------------------------------------------------
    */

    const printTemplateOptions = [
      {
        value:
          INVOICE_TEMPLATE_CODES
            .MODERN_GST,

        label:
          "Modern GST",
      },

      {
        value:
          INVOICE_TEMPLATE_CODES
            .COMPACT,

        label:
          "Compact",
      },

      {
        value:
          INVOICE_TEMPLATE_CODES
            .RETAIL,

        label:
          "Retail",
      },

      {
        value:
          INVOICE_TEMPLATE_CODES
            .SERVICE,

        label:
          "Service",
      },

      {
        value:
          INVOICE_TEMPLATE_CODES
            .THERMAL,

        label:
          "Thermal (80mm)",
      },

      {
        value:
          INVOICE_TEMPLATE_CODES
            .PROFESSIONAL,

        label:
          "Professional",
      },

      {
        value:
          INVOICE_TEMPLATE_CODES
            .MINIMAL,

        label:
          "Minimal",
      },
    ];


    const handleTemplateChange =
      useCallback(
        (
          event
        ) => {
          const nextTemplate =
            normalizeInvoiceTemplateCode(
              event.target.value
            );

          /*
          | Update only the template query parameter.
          | Existing mode/print/download query parameters remain untouched.
          */

          const params =
            new URLSearchParams(
              location.search
            );

          params.set(
            "template",
            nextTemplate
          );

          history.replace({
            pathname:
              location.pathname,

            search:
              `?${params.toString()}`,
          });
        },
        [
          history,
          location.pathname,
          location.search,
        ]
      );


    /*
    |--------------------------------------------------------------------------
    | SAFE NATIVE PRINT
    |--------------------------------------------------------------------------
    |
    | Why this exists:
    |
    | The invoice is rendered inside the Admin Console. Parent layout wrappers
    | can have overflow:hidden, transforms, fixed heights or global @media
    | print rules. A direct window.print() can therefore produce a blank page.
    |
    | Before printing we temporarily:
    | - force the invoice page and its ancestors into a printable state
    | - add a body class used by SaleInvoicePrintPage.css for print isolation
    | - wait for fonts and invoice images
    | - call window.print()
    | - restore every inline style after the print dialog closes
    |
    | This preserves the previous on-screen layout after printing.
    |
    |--------------------------------------------------------------------------
    */

    const printInvoice =
      useCallback(
        async () => {
          const printableNode =
            printRef.current;

          if (!printableNode) {
            setActionMessage(
              "Invoice preview is not ready to print."
            );

            return false;
          }

          const printPage =
            printableNode.closest(
              ".sale-invoice-print-page"
            ) ||
            printableNode;

          const touchedElements =
            [];

          let current =
            printPage;

          while (
            current &&
            current !==
              document.body
          ) {
            touchedElements.push({
              element:
                current,

              cssText:
                current.getAttribute(
                  "style"
                ),
            });

            current =
              current.parentElement;
          }

          const forcePrintable =
            (
              element
            ) => {
              if (!element) {
                return;
              }

              element.style.setProperty(
                "display",
                "block",
                "important"
              );

              element.style.setProperty(
                "visibility",
                "visible",
                "important"
              );

              element.style.setProperty(
                "opacity",
                "1",
                "important"
              );

              element.style.setProperty(
                "height",
                "auto",
                "important"
              );

              element.style.setProperty(
                "min-height",
                "0",
                "important"
              );

              element.style.setProperty(
                "max-height",
                "none",
                "important"
              );

              element.style.setProperty(
                "overflow",
                "visible",
                "important"
              );

              element.style.setProperty(
                "transform",
                "none",
                "important"
              );
            };

          touchedElements.forEach(
            ({
              element,
            }) => {
              forcePrintable(
                element
              );
            }
          );

          document.body
            .classList
            .add(
              "sale-invoice-native-print"
            );

          let cleanedUp =
            false;

          const cleanup =
            () => {
              if (
                cleanedUp
              ) {
                return;
              }

              cleanedUp =
                true;

              window.removeEventListener(
                "afterprint",
                cleanup
              );

              document.body
                .classList
                .remove(
                  "sale-invoice-native-print"
                );

              touchedElements.forEach(
                ({
                  element,
                  cssText,
                }) => {
                  if (
                    cssText ===
                    null
                  ) {
                    element.removeAttribute(
                      "style"
                    );
                  } else {
                    element.setAttribute(
                      "style",
                      cssText
                    );
                  }
                }
              );
            };

          window.addEventListener(
            "afterprint",
            cleanup,
            {
              once:
                true,
            }
          );

          try {
            if (
              document.fonts
                ?.ready
            ) {
              await document
                .fonts
                .ready;
            }

            const images =
              Array.from(
                printableNode
                  .querySelectorAll(
                    "img"
                  )
              );

            await Promise.all(
              images.map(
                (
                  image
                ) => {
                  if (
                    image.complete
                  ) {
                    return Promise
                      .resolve();
                  }

                  return new Promise(
                    (
                      resolve
                    ) => {
                      const done =
                        () => {
                          image
                            .removeEventListener(
                              "load",
                              done
                            );

                          image
                            .removeEventListener(
                              "error",
                              done
                            );

                          resolve();
                        };

                      image
                        .addEventListener(
                          "load",
                          done
                        );

                      image
                        .addEventListener(
                          "error",
                          done
                        );
                    }
                  );
                }
              )
            );

            /*
            | Give Chrome two frames to recalculate layout after temporary
            | print styles have been applied.
            */

            await new Promise(
              (
                resolve
              ) =>
                window
                  .requestAnimationFrame(
                    () =>
                      window
                        .requestAnimationFrame(
                          resolve
                        )
                  )
            );

            window.focus();
            window.print();

            /*
            | afterprint normally performs cleanup. This fallback prevents a
            | browser bug from leaving the page in print mode indefinitely.
            */

            window.setTimeout(
              cleanup,
              60000
            );

            return true;
          } catch (
            error
          ) {
            console.error(
              "Invoice print failed:",
              error
            );

            cleanup();

            setActionMessage(
              "Unable to prepare invoice for printing."
            );

            return false;
          }
        },
        []
      );


    /*
    |--------------------------------------------------------------------------
    | AUTOMATIC PRINT MODE
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
      const params =
        new URLSearchParams(
          location.search
        );

      const mode =
        params.get(
          "mode"
        ) ||
        params.get(
          "print"
        );

      if (
        saleInvoice &&
        !loading &&
        (
          mode === "print" ||
          mode === "true"
        )
      ) {
        const timeout =
          window.setTimeout(
            () => {
              printInvoice();
            },
            450
          );

        return () => {
          window.clearTimeout(
            timeout
          );
        };
      }

      return undefined;
    }, [
      saleInvoice,
      loading,
      location.search,
      printInvoice,
    ]);

    /*
    |--------------------------------------------------------------------------
    | ACTIONS
    |--------------------------------------------------------------------------
    */

    const handlePrint =
      useCallback(
        async () => {
          setActionMessage(
            ""
          );

          await printInvoice();
        },
        [
          printInvoice,
        ]
      );


    const handleDownload =
      useCallback(
        async () => {
          setActionMessage(
            "Choose “Save as PDF” in the print dialog to download the sale invoice."
          );

          await printInvoice();
        },
        [
          printInvoice,
        ]
      );

    const handleWhatsAppShare =
      () => {
        const text = [
          `${company.name} - Tax Invoice ${documentNumber}`,

          `Customer: ${customer.name}`,

          `Grand Total: ${formatCurrency(
            totals.grandTotal
          )}`,

          `View: ${window.location.href}`,
        ].join("\n");

        const url =
          `https://wa.me/?text=${encodeURIComponent(
            text
          )}`;

        window.open(
          url,
          "_blank",
          "noopener,noreferrer"
        );
      };

    /*
    |--------------------------------------------------------------------------
    | LOADING / EMPTY
    |--------------------------------------------------------------------------
    */

    if (
      loading &&
      !saleInvoice
    ) {
      return (
        <div className="sale-invoice-print-state">
          <FiRefreshCw className="sale-invoice-print-spin" />

          <strong>
            Loading sale invoice...
          </strong>
        </div>
      );
    }

    if (!saleInvoice) {
      return (
        <div className="sale-invoice-print-state">
          <strong>
            Sale Invoice not found.
          </strong>

          <button
            type="button"
            onClick={() =>
              history.push(
                "/billing/sale-invoices"
              )
            }
          >
            Back to Sale Invoices
          </button>
        </div>
      );
    }

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
      <div className="sale-invoice-print-page">
        <div className="sale-invoice-print-toolbar">
          <button
            type="button"
            className="sale-invoice-print-toolbar-back"
            onClick={() =>
              history.goBack()
            }
          >
            <FiArrowLeft />

            Back
          </button>

          <div className="sale-invoice-print-template-selector">

            <label
              htmlFor="sale-invoice-print-template"
            >
              Template
            </label>

            <select
              id="sale-invoice-print-template"
              value={
                selectedTemplateCode
              }
              onChange={
                handleTemplateChange
              }
            >
              {printTemplateOptions.map(
                (
                  option
                ) => (
                  <option
                    key={
                      option.value
                    }
                    value={
                      option.value
                    }
                  >
                    {option.label}
                  </option>
                )
              )}
            </select>

          </div>

          <div className="sale-invoice-print-toolbar-actions">
            <button
              type="button"
              onClick={
                handleWhatsAppShare
              }
            >
              <FiMessageCircle />

              WhatsApp
            </button>

            <button
              type="button"
              onClick={
                handleDownload
              }
            >
              <FiDownload />

              Download PDF
            </button>

            <button
              type="button"
              className="sale-invoice-print-primary-action"
              onClick={
                handlePrint
              }
            >
              <FiPrinter />

              Print
            </button>
          </div>
        </div>

        {actionMessage && (
          <div className="sale-invoice-print-action-message">
            {actionMessage}
          </div>
        )}

        {SelectedInvoiceTemplate ? (

          <main
            ref={
              printRef
            }
            className={[
              "sale-invoice-print-sheet",
              "sale-invoice-print-sheet-template",
              selectedTemplateCode ===
                INVOICE_TEMPLATE_CODES
                  .THERMAL
                ? "sale-invoice-print-sheet-thermal"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            id="sale-invoice-print-sheet"
          >

            <SelectedInvoiceTemplate
              {...templateProps}
            />

          </main>

        ) : (

        <main
          ref={
            printRef
          }
          className="sale-invoice-print-sheet"
          id="sale-invoice-print-sheet"
        >
          <header className="sale-invoice-print-header">
            <div className="sale-invoice-print-company">
              {resolveBusinessLogo(
                templateProps
                  .businessSnapshot
              ) ? (
                <img
                  src={
                    resolveBusinessLogo(
                      templateProps
                        .businessSnapshot
                    )
                  }
                  alt={`${company.name} logo`}
                  className="sale-invoice-print-company-logo"
                />
              ) : (
                <div className="sale-invoice-print-company-initial">
                  {getInitial(
                    company.name
                  )}
                </div>
              )}

              <div>
                <h1>
                  {company.name}
                </h1>

                <p>
                  {company.address}
                </p>

                {company.mobile && (
                  <strong>
                    Mobile:{" "}
                    {company.mobile}
                  </strong>
                )}

                {company.email && (
                  <span>
                    Email:{" "}
                    {company.email}
                  </span>
                )}

                {company.gstin && (
                  <span>
                    GSTIN:{" "}
                    {company.gstin}
                  </span>
                )}
              </div>
            </div>

            <div className="sale-invoice-print-document-title">
              <h2>
                TAX INVOICE
              </h2>

              <span
                className={`sale-invoice-print-status sale-invoice-print-status-${String(
                  status
                ).toLowerCase()}`}
              >
                {formatLabel(
                  status
                )}
              </span>
            </div>
          </header>

          <div className="sale-invoice-print-accent-line" />

          <section className="sale-invoice-print-party-grid">
            <div className="sale-invoice-print-party-box">
              <span className="sale-invoice-print-box-label">
                BILL TO
              </span>

              <strong>
                {customer.name}
              </strong>

              <p>
                {customer.billingAddress}
              </p>

              {customer.mobile && (
                <span>
                  Mobile:{" "}
                  {customer.mobile}
                </span>
              )}

              {customer.email && (
                <span>
                  Email:{" "}
                  {customer.email}
                </span>
              )}

              {customer.gstin && (
                <span>
                  GSTIN:{" "}
                  {customer.gstin}
                </span>
              )}
            </div>

            <div className="sale-invoice-print-party-box">
              <span className="sale-invoice-print-box-label">
                SHIP TO
              </span>

              <strong>
                {customer.name}
              </strong>

              <p>
                {customer.shippingAddress}
              </p>
            </div>

            <div className="sale-invoice-print-meta-box">
              <div>
                <span>
                  Invoice No.
                </span>

                <strong>
                  {documentNumber}
                </strong>
              </div>

              <div>
                <span>
                  Invoice Date
                </span>

                <strong>
                  {formatDate(
                    documentDate
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Due Date
                </span>

                <strong>
                  {formatDate(
                    dueDate
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Sales Channel
                </span>

                <strong>
                  {formatLabel(
                    salesChannel
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Price Mode
                </span>

                <strong>
                  {String(
                    priceMode
                  ).toUpperCase() ===
                  "TAX_INCLUSIVE"
                    ? "Tax Inclusive"
                    : String(
                        priceMode
                      ).toUpperCase() ===
                      "TAX_EXCLUSIVE"
                      ? "Tax Exclusive"
                      : formatLabel(
                          priceMode
                        )}
                </strong>
              </div>
            </div>
          </section>

          <section className="sale-invoice-print-items">
            <table>
              <thead>
                <tr>
                  <th>
                    S.No
                  </th>

                  <th>
                    Item
                  </th>

                  <th>
                    HSN/SAC
                  </th>

                  <th>
                    Qty
                  </th>

                  <th>
                    Rate
                  </th>

                  <th>
                    Discount
                  </th>

                  <th>
                    Tax
                  </th>

                  <th>
                    Amount
                  </th>
                </tr>
              </thead>

              <tbody>
                {items.length ===
                0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="sale-invoice-print-empty-items"
                    >
                      No items available.
                    </td>
                  </tr>
                ) : (
                  items.map(
                    (
                      item,
                      index
                    ) => (
                      <tr
                        key={
                          item.id
                        }
                      >
                        <td>
                          {index + 1}
                        </td>

                        <td>
                          <strong>
                            {item.itemName}
                          </strong>

                          {item.description &&
                            item.description !==
                              item.itemName && (
                              <small>
                                {item.description}
                              </small>
                            )}

                          {item.isManual && (
                            <small className="sale-invoice-print-manual-item">
                              Manual Item
                            </small>
                          )}
                        </td>

                        <td>
                          {item.hsnSacCode}
                        </td>

                        <td>
                          {item.quantity}
                          {item.unit &&
                          item.unit.toLowerCase() !==
                            "none"
                            ? ` ${String(
                                item.unit
                              ).toUpperCase()}`
                            : ""}
                        </td>

                        <td>
                          {formatCurrency(
                            item.rate
                          )}
                        </td>

                        <td>
                          {item.discountAmount >
                          0 ? (
                            <>
                              <strong>
                                {formatCurrency(
                                  item.discountAmount
                                )}
                              </strong>

                              {String(
                                item.discountType
                              ).toUpperCase() ===
                                "PERCENTAGE" && (
                                <small>
                                  {item.discountValue}%
                                </small>
                              )}

                              {String(
                                item.discountType
                              ).toUpperCase() ===
                                "FLAT" && (
                                <small>
                                  Flat
                                </small>
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          {item.taxAmount >
                          0 ? (
                            <>
                              <strong>
                                {formatCurrency(
                                  item.taxAmount
                                )}
                              </strong>

                              {item.taxPercentage >
                                0 && (
                                <small>
                                  {item.taxPercentage}%
                                </small>
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          <strong>
                            {formatCurrency(
                              item.totalAmount
                            )}
                          </strong>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          </section>

          <section className="sale-invoice-print-bottom">
            <div className="sale-invoice-print-notes">
              <div>
                <h3>
                  Notes
                </h3>

                <p>
                  {notes ||
                    "No notes added."}
                </p>
              </div>

              <div>
                <h3>
                  Terms & Conditions
                </h3>

                <p>
                  {terms ||
                    "No terms and conditions added."}
                </p>
              </div>
            </div>

            <div className="sale-invoice-print-summary">
              <div>
                <span>
                  Subtotal
                </span>

                <strong>
                  {formatCurrency(
                    totals.subtotal
                  )}
                </strong>
              </div>

              <div className="sale-invoice-print-discount-row">
                <span>
                  Total Discount
                </span>

                <strong>
                  {totals.totalDiscount >
                  0
                    ? `- ${formatCurrency(
                        totals.totalDiscount
                      )}`
                    : formatCurrency(
                        0
                      )}
                </strong>
              </div>

              <div>
                <span>
                  Taxable Amount
                </span>

                <strong>
                  {formatCurrency(
                    totals.taxableAmount
                  )}
                </strong>
              </div>

              {totals.isInterState ? (
                <div>
                  <span>
                    IGST
                  </span>

                  <strong>
                    {formatCurrency(
                      totals.igstAmount
                    )}
                  </strong>
                </div>
              ) : (
                <>
                  <div>
                    <span>
                      CGST
                    </span>

                    <strong>
                      {formatCurrency(
                        totals.cgstAmount
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>
                      SGST
                    </span>

                    <strong>
                      {formatCurrency(
                        totals.sgstAmount
                      )}
                    </strong>
                  </div>
                </>
              )}

              <div>
                <span>
                  Total Tax
                </span>

                <strong>
                  {formatCurrency(
                    totals.totalTax
                  )}
                </strong>
              </div>

              {totals.roundOffAmount !==
                0 && (
                <div>
                  <span>
                    Round Off
                  </span>

                  <strong>
                    {formatCurrency(
                      totals.roundOffAmount
                    )}
                  </strong>
                </div>
              )}

              <div className="sale-invoice-print-grand-total">
                <span>
                  Grand Total
                </span>

                <strong>
                  {formatCurrency(
                    totals.grandTotal
                  )}
                </strong>
              </div>
            </div>
          </section>

          <footer className="sale-invoice-print-footer">
            <span>
              This is a computer-generated saleInvoice.
            </span>

            <InvoiceSignature
              businessSnapshot={
                templateProps
                  .businessSnapshot
              }
            />
          </footer>
        </main>

        )}

      </div>
    );
  };

export default SaleInvoicePrintPage;